import * as React from 'react'
import { ReactElement, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Button, DialogActions, DialogBody, DialogContent, DialogTitle, MessageBar, mergeClasses } from '@fluentui/react-components'
import { DismissRegular } from '@fluentui/react-icons'
import { Selection } from '@fluentui/react'
import { SelectionMode } from '@fluentui/react/lib/Selection'
import { Message } from '@microsoft/microsoft-graph-types'
import { useTranslation } from 'react-i18next'
import { HammockDialogProps } from '../HammockDialog'
import ProcessEmailForm from './ProcessEmailForm'
import { EmailProcessingAction, EmailProcessingActionIconMap, EmailProcessingActionLabelMap } from '../../../config/emailProcessingConfig'
import { SelectableEmail } from '../../../../hooks/email/useProcessEmail'
import { mapMessages } from '../../../../components/Email/EmailSearchSelectList'
import Loading from '../../../../components/Loading'
import { Destination } from '../../../../context/AxiosContext'
import AuthorizationContext from '../../../../context/AuthorizationContext'
import { FILE_REFERENCE_NUMBER_PROP } from '../../../../helpers/referenceNumberHelper'
import useAppToast from '../../../../hooks/useAppToast'
import useEntity from '../../../../hooks/useEntity'
import useUser from '../../../../hooks/useUser'
import { useApplicationStyles } from '../../../../pages/App'

/** What the inboxer sends over the websocket: the message it wants filed, and the mailbox it lives in. */
export type EmailFilingSuggestionDto = {
    messageId: string
    userEmail: string
}

const FileIcon = EmailProcessingActionIconMap.get(EmailProcessingAction.File)

/**
 * The popup the inboxer raises to file a single e-mail (HAM-4278). Replaces the Fluent v8 CommandDialog this used to
 * be: that one rendered in a v8 Layer outside the FluentProvider, which left it light in dark mode. Hosted by
 * PopupContext, because there is no overview behind it to hang a dialog on.
 */
export default function EmailFilingSuggestionDialogBody(props: HammockDialogProps<EmailFilingSuggestionDto>): ReactElement {
    const { t: translate } = useTranslation()
    const appStyles = useApplicationStyles()
    const toast = useAppToast()
    const { currentMailbox, currentEmail } = useUser()
    const { host: currentHost } = useContext(AuthorizationContext)

    const submitForm = useRef<() => Promise<void>>()
    const [isSubmitting, setIsSubmitting] = useState<boolean>()

    const dialogBodyProps = useMemo(() => props.dialogBodyProps?.() ?? ({} as EmailFilingSuggestionDto), [props])

    // null keeps useEntity from firing; false would mean "this endpoint needs no id" and would still request a message.
    const [message] = useEntity<Message>(
        (itemId: string) => ({
            method: 'GET',
            url: `/messages/${itemId}`,
            baseURL: Destination.Email,
            headers: { SuppressErrors: 'true', 'If-None-Match': null, Prefer: 'IdType="ImmutableId"' },
            params: {
                $select:
                    'id,internetMessageId,conversationId,subject,bodyPreview,webLink,hasAttachments,sender,toRecipients,sentDateTime,receivedDateTime',
                // Carries the file references the e-mail already has, so we can tell it was filed before.
                $expand: `singleValueExtendedProperties($filter=id eq '${FILE_REFERENCE_NUMBER_PROP}')`,
            },
        }),
        dialogBodyProps.messageId ?? null,
    )

    // mapMessages drops drafts, so this is undefined for a message we cannot file at all.
    const email = useMemo<SelectableEmail>(
        () =>
            message.loaded && message.data
                ? (mapMessages([message.data], currentEmail, currentEmail, currentHost, Destination.Email)[0] as SelectableEmail)
                : undefined,
        [message, currentEmail, currentHost],
    )

    const selection = useMemo<Selection<SelectableEmail>>(() => {
        if (!email) {
            return new Selection<SelectableEmail>()
        }

        const select = new Selection<SelectableEmail>({
            items: [email],
            onSelectionChanged: () => {},
            getKey: item => item.id,
            selectionMode: SelectionMode.single,
        })
        select.setIndexSelected(0, true, true)
        return select
    }, [email])

    // The e-mail was removed or moved while the suggestion was on its way.
    useEffect(() => {
        if (message.rejected || (message.loaded && !email)) {
            toast.showMessage(translate('email.filing.error.gone'), { intent: 'error' })
            props.onDismiss?.()
        }
    }, [message.rejected, message.loaded, email])

    const references = email?.references ?? []
    // Its own key rather than the toolbar's "E-mail {{action}}": that one borrows the button label, which is
    // capitalised and reads wrong inside a sentence-case title.
    const title = translate('email.filing.dialog.manual.title')

    if (!message.loaded || !email) {
        return (
            <DialogBody>
                <DialogTitle>{title}</DialogTitle>
                <Loading />
                <DialogActions />
            </DialogBody>
        )
    }

    return (
        <DialogBody>
            <DialogTitle>{title}</DialogTitle>
            {/* The form spaces its own children; this gives the warning above it the same gap. */}
            <DialogContent className={mergeClasses(appStyles.flexColumn, appStyles.gapS)}>
                {references.length > 0 && (
                    <MessageBar intent={'warning'} layout={'multiline'}>
                        {translate('email.filing.already_filed', { references: references.join(', ') })}
                    </MessageBar>
                )}
                <ProcessEmailForm
                    emailMailbox={currentMailbox}
                    action={EmailProcessingAction.File}
                    selection={selection}
                    // Filing replaces the references, so the ones the e-mail already has are pre-selected: filing it a
                    // second time then adds a file rather than silently dropping the first one.
                    initialReferences={references}
                    onDismiss={() => props.onDismiss?.()}
                    setSubmitForm={submit => (submitForm.current = submit)}
                    setIsSubmitting={submitting => setIsSubmitting(submitting)}
                />
            </DialogContent>
            <DialogActions>
                <Button appearance={'transparent'} icon={<DismissRegular />} onClick={() => props.onDismiss?.()}>
                    {translate('action.cancel')}
                </Button>
                <Button appearance={'primary'} icon={<FileIcon />} disabled={isSubmitting} onClick={() => submitForm.current?.()}>
                    {translate(EmailProcessingActionLabelMap.get(EmailProcessingAction.File))}
                </Button>
            </DialogActions>
        </DialogBody>
    )
}
